import { valibotResolver } from "@hookform/resolvers/valibot";
import { useForm } from "react-hook-form";

import { Button } from "common/components/ui/Button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from "common/components/ui/Dialog";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
  DrawerTrigger,
} from "common/components/ui/Drawer";
import FormField from "common/components/ui/FormField";
import { Input } from "common/components/ui/Input";
import useWorkspaceMutations from "common/datahooks/useWorkspaceMutations";
import useWorkspaces from "common/datahooks/useWorkspaces";
import useGTM from "common/hooks/useGTM";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import useWorkspaceNavigate from "common/hooks/useWorkspaceNavigate";
import { WorkspaceNameSchema } from "common/schemas";

import type { ReactNode } from "react";

import CreateWorkspaceImage from "assets/images/create-workspace.png";

interface CreateWorkspaceModalProps {
  triggerButton: ReactNode;
}

export default function CreateWorkspaceModal({
  triggerButton,
}: CreateWorkspaceModalProps) {
  const isDesktop = useTwBreakpoint("lg");

  const { sendEvent } = useGTM();
  const { goToWorkspace } = useWorkspaceNavigate();
  const { createWorkspace, isCreatingWorkspace } = useWorkspaceMutations();
  const { workspaces } = useWorkspaces();

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: "",
    },
    resolver: valibotResolver(WorkspaceNameSchema),
  });

  function onSubmit({ name }: { name: string }) {
    if (workspaces.find((workspace) => workspace.name === name.trim())) {
      setError("name", { message: "Workspace with this name already exists" });
    } else {
      createWorkspace({ name }).then((workspace) => {
        sendEvent("workspace_created");
        goToWorkspace(workspace.id, { replace: true });
      });
    }
  }

  const Component = isDesktop ? Dialog : Drawer;
  const Content = isDesktop ? DialogContent : DrawerContent;
  const Title = isDesktop ? DialogTitle : DrawerTitle;
  const Description = isDesktop ? DialogDescription : DrawerDescription;
  const Trigger = isDesktop ? DialogTrigger : DrawerTrigger;
  const Close = isDesktop ? DialogClose : DrawerClose;

  return (
    <Component>
      <Trigger asChild>{triggerButton}</Trigger>
      <Content className="lg:w-[448px]">
        <img
          src={CreateWorkspaceImage}
          alt="Workspace Creation"
          height={195}
          width={280}
          className="mx-auto"
        />

        <Title>Create a new workspace</Title>

        <Description className="mb-8">
          This will be the main place to manage your Aimfox campaigns and
          collaborate with your team
        </Description>

        <FormField
          htmlFor="workspaceName"
          label="Workspace name"
          className="mb-8"
          error={errors.name?.message}
          size="lg"
        >
          <Input
            variant="lg"
            id="workspaceName"
            placeholder="Name your workspace"
            {...register("name")}
          />
        </FormField>

        <div className="flex justify-between gap-x-2.5">
          <Close asChild>
            <Button variant="secondary-black" className="lg:flex-1">
              Cancel
            </Button>
          </Close>

          <Button
            className="ml-auto lg:flex-1"
            onClick={handleSubmit(onSubmit)}
            isLoading={isCreatingWorkspace}
          >
            Create
          </Button>
        </div>
      </Content>
    </Component>
  );
}
